/**
 * Temporary preview page to demonstrate multilingual consent forms
 * This page shows both the Cookie Consent and Parent Registration Consent
 * in both English and Hindi for demonstration purposes.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Globe, Shield, Cookie } from "lucide-react";
import { SUPPORTED_LANGUAGES, getSectionTranslations } from "@/i18n";

export default function ConsentPreview() {
  const [cookieLang, setCookieLang] = useState('en');
  const [consentLang, setConsentLang] = useState('en');
  const [showCookieLangSelector, setShowCookieLangSelector] = useState(false);
  const [showConsentLangSelector, setShowConsentLangSelector] = useState(false);
  
  const cookieT = getSectionTranslations(cookieLang, 'cookie');
  const consentT = getSectionTranslations(consentLang, 'consent');
  
  const cookieLangObj = SUPPORTED_LANGUAGES.find(l => l.code === cookieLang);
  const consentLangObj = SUPPORTED_LANGUAGES.find(l => l.code === consentLang);

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center mb-2">Multilingual Consent Preview</h1>
        <p className="text-gray-600 text-center mb-8">Preview consent forms in English and Hindi</p>
        
        {/* Cookie Consent Banner Preview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Cookie className="w-5 h-5 text-emerald-600" />
            Cookie Consent Banner
          </h2>
          
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            {/* Language Selector */}
            <div className="flex justify-end mb-4">
              <div className="relative">
                <button
                  onClick={() => setShowCookieLangSelector(!showCookieLangSelector)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  data-testid="preview-cookie-language-selector"
                >
                  <Globe className="w-4 h-4" />
                  <span>{cookieLangObj?.nativeName}</span>
                </button>
                
                {showCookieLangSelector && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] z-10">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCookieLang(lang.code);
                          setShowCookieLangSelector(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                          cookieLang === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                        }`}
                        data-testid={`preview-cookie-lang-${lang.code}`}
                      >
                        <span>{lang.nativeName}</span>
                        {cookieLang === lang.code && (
                          <span className="text-emerald-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  {cookieT.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {cookieT.description}{" "}
                  <span className="text-emerald-600 hover:underline cursor-pointer">
                    {cookieT.readPrivacyPolicy}
                  </span>
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    {cookieT.acceptAll}
                  </Button>
                  <Button variant="outline">
                    {cookieT.necessaryOnly}
                  </Button>
                  <Button variant="ghost" className="text-gray-600">
                    {cookieT.managePreferences}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Parent Registration Consent Preview */}
        <div>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Parent Registration Consent (DPDP Compliance)
          </h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            {/* Language Selector for Consent */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {consentT.title}
              </h4>
              
              {/* Language dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowConsentLangSelector(!showConsentLangSelector)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                  data-testid="preview-consent-language-selector"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{consentLangObj?.nativeName}</span>
                </button>
                
                {showConsentLangSelector && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px] z-10">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setConsentLang(lang.code);
                          setShowConsentLangSelector(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 flex items-center justify-between ${
                          consentLang === lang.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        data-testid={`preview-consent-lang-${lang.code}`}
                      >
                        <span>{lang.nativeName}</span>
                        {consentLang === lang.code && (
                          <span className="text-blue-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 accent-blue-600 w-4 h-4"
              />
              <span className="text-gray-700">
                {consentT.intro}
                <ul className="list-disc ml-5 mt-2 text-gray-600 space-y-1">
                  {consentT.points && consentT.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
                <span className="block mt-3 text-xs text-gray-500">
                  {consentT.withdrawNote}
                </span>
              </span>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>This is a preview page for demonstration purposes.</p>
          <p>The actual consent forms appear during cookie acceptance and parent registration.</p>
        </div>
      </div>
    </div>
  );
}
